import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { PhoneOff, Mic, MicOff, Volume2, Hand } from 'lucide-react';
import { cn, API_BASE_URL } from '../../lib/utils';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import AiMembershipRenewalDialog from '../../components/customer/AiMembershipRenewalDialog';
import {
  isCustomerAiMembershipActive,
  AI_MEMBERSHIP_REQUIRED_CODE,
} from '../../lib/customerMembership';

const SOCKET_URL = API_BASE_URL.replace('/api/v1', '');

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN', recognition: 'en-US', ttsLocale: 'en-US' },
  { code: 'ar', label: 'Arabic',  short: 'AR', recognition: 'ar-SA', ttsLocale: 'ar-SA' },
  { code: 'hi', label: 'Hindi',   short: 'HI', recognition: 'hi-IN', ttsLocale: 'hi-IN' },
  { code: 'tr', label: 'Turkish', short: 'TR', recognition: 'tr-TR', ttsLocale: 'tr-TR' },
  { code: 'ur', label: 'Urdu',    short: 'UR', recognition: 'ur-PK', ttsLocale: 'ur-IN' },
  { code: 'es', label: 'Spanish', short: 'ES', recognition: 'es-ES', ttsLocale: 'es-ES' },
  { code: 'de', label: 'German',  short: 'DE', recognition: 'de-DE', ttsLocale: 'de-DE' },
  { code: 'fr', label: 'French',  short: 'FR', recognition: 'fr-FR', ttsLocale: 'fr-FR' },
];

/**
 * Detect the appropriate TTS language from the AI response text.
 * Priority: script detection (Arabic/Hindi) > mic language fallback.
 * For Latin-script text with Urdu mic (Roman Urdu), use 'en' since
 * the Urdu TTS voice only reads Arabic-script Urdu, not Roman Urdu.
 */
function detectTtsLang(text, micLang) {
  const nonWs = text.replace(/\s+/g, '').length;
  if (nonWs === 0) return micLang || 'en';
  const arabicRatio  = (text.match(/[؀-ۿ]/g) || []).length / nonWs;
  const hindiRatio   = (text.match(/[ऀ-ॿ]/g) || []).length / nonWs;
  if (arabicRatio > 0.12) return 'ar';
  if (hindiRatio  > 0.12) return 'hi';
  if (micLang === 'ur') return 'en';
  return micLang || 'en';
}

// ── Animated orb ──────────────────────────────────────────────────────────────
const Orb = ({ state }) => {
  const configs = {
    idle:       { color: '#a78bfa', speed: '3s' },
    listening:  { color: '#60a5fa', speed: '1.5s' },
    processing: { color: '#f59e0b', speed: '0.8s' },
    speaking:   { color: '#34d399', speed: '0.6s' },
    muted:      { color: '#94a3b8', speed: '4s' },
  };
  const { color, speed } = configs[state] || configs.idle;

  return (
    <div className="relative flex items-center justify-center" style={{ width: 220, height: 220 }}>
      {(state === 'speaking' || state === 'listening') && (
        <>
          <div className="absolute rounded-full opacity-20 animate-ping"
            style={{ width: 220, height: 220, backgroundColor: color, animationDuration: speed }} />
          <div className="absolute rounded-full opacity-10 animate-ping"
            style={{ width: 180, height: 180, backgroundColor: color, animationDuration: speed, animationDelay: '0.3s' }} />
        </>
      )}
      {state === 'processing' && (
        <div className="absolute rounded-full border-4 animate-spin"
          style={{ width: 200, height: 200, borderColor: color, borderTopColor: 'transparent', animationDuration: '1s' }} />
      )}
      <div
        className="relative rounded-full flex items-center justify-center transition-all duration-500"
        style={{
          width: 140, height: 140,
          background: `radial-gradient(circle at 40% 35%, ${color}ff, ${color}99)`,
          boxShadow: `0 0 60px ${color}22, 0 0 120px ${color}11, inset 0 1px 0 rgba(255,255,255,0.3)`,
          animation: `breathe ${speed} ease-in-out infinite`,
        }}
      >
        <div className="absolute top-5 left-7 w-8 h-4 rounded-full bg-card/30 blur-sm" />
        {state === 'muted'      && <MicOff className="w-8 h-8 text-white/80" />}
        {state === 'processing' && <span className="text-white text-2xl font-light">···</span>}
      </div>
      <style>{`@keyframes breathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }`}</style>
    </div>
  );
};

const STATUS_LABEL = {
  idle:       'AI Assistant',
  listening:  'Listening...',
  processing: 'Thinking...',
  speaking:   'Speaking...',
  muted:      'Muted',
};

function useTimer(running) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!running) { setSecs(0); return; }
    const id = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  return `${String(Math.floor(secs / 60)).padStart(2,'0')}:${String(secs % 60).padStart(2,'0')}`;
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function VoiceChatPage() {
  const navigate  = useNavigate();
  const token     = localStorage.getItem('customer_token');
  const { user, refreshUser } = useCustomerAuth();
  const { branding } = useBranding();
  const [showRenewalDialog, setShowRenewalDialog] = useState(false);

  const aiMembershipActive = isCustomerAiMembershipActive(user);

  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  const [callState, setCallState]   = useState('idle');
  const [active, setActive]         = useState(false);
  const [muted, setMuted]           = useState(false);
  const [language, setLanguage]     = useState('en');
  const [transcript, setTranscript] = useState('');
  const [aiText, setAiText]         = useState('');
  const [hasTts, setHasTts]         = useState(true);
  const [error, setError]           = useState('');
  const [mealLogged, setMealLogged] = useState(null);

  const langRef = useRef('en');

  const duration = useTimer(active);

  const socketRef         = useRef(null);
  const recogRef          = useRef(null);
  const audioRef          = useRef(null);
  const mediaStreamRef    = useRef(null); // echo-cancelled getUserMedia stream
  const stateRef          = useRef(callState);
  const muteRef           = useRef(muted);
  const activeRef         = useRef(false);
  const sessionRef        = useRef(null);
  const speakResponseRef  = useRef(null);
  const startListeningRef = useRef(null);

  useEffect(() => { stateRef.current = callState; }, [callState]);
  useEffect(() => { muteRef.current = muted; }, [muted]);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { langRef.current = language; }, [language]);

  // ── TTS config ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/customer/ai-chat/config`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(d => setHasTts(d?.tts?.enabled || false)).catch(() => {});
  }, [token]);

  // ── Socket setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const sock = io(`${SOCKET_URL}/ai-chat`, {
      auth: { token }, query: { token },
      path: '/api/socket.io',
      transports: ['polling', 'websocket'],
    });
    sock.on('ai_chat:session_created', ({ session_id }) => { sessionRef.current = session_id; });
    sock.on('ai_chat:session_ready', ({ session_id }) => { sessionRef.current = session_id; });
    sock.on('ai_chat:done',  ({ full_text }) => { setAiText(full_text); speakResponseRef.current?.(full_text); });
    sock.on('ai_chat:error', ({ message, code }) => {
      if (code === AI_MEMBERSHIP_REQUIRED_CODE) {
        setShowRenewalDialog(true);
        refreshUser?.();
      }
      setError(message);
      setCallState('listening');
      startListeningRef.current?.();
    });
    sock.on('ai_chat:meal_logged', (data) => {
      setMealLogged(data);
      setTimeout(() => setMealLogged(null), 4000);
    });
    socketRef.current = sock;
    return () => sock.disconnect();
  }, [token, refreshUser]);

  // ── Interrupt AI speech ───────────────────────────────────────────────────
  const interruptSpeech = useCallback(() => {
    window.speechSynthesis.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
  }, []);

  // ── Stop microphone recognition ───────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (recogRef.current) {
      try { recogRef.current.stop(); } catch {}
      recogRef.current = null;
    }
  }, []);

  // ── Send to AI ────────────────────────────────────────────────────────────
  const sendToAI = useCallback((text) => {
    if (!aiMembershipActive) {
      setShowRenewalDialog(true);
      return;
    }
    if (!socketRef.current?.connected) { setError('Not connected.'); return; }
    setCallState('processing');
    setTranscript('');
    setAiText('');
    const now = new Date();
    socketRef.current.emit('ai_chat:message', {
      session_id: sessionRef.current || undefined,
      message: text,
      language: langRef.current,
      user_local_hour: now.getHours(),
      user_local_minute: now.getMinutes(),
      date: now.toISOString(),
    });
  }, [aiMembershipActive]);

  // ── Speech Recognition ────────────────────────────────────────────────────
  // Mic is ONLY active when state is 'listening'. Stopped while AI speaks.
  // This completely prevents the echo feedback loop.
  const startListening = useCallback(() => {
    if (muteRef.current || !activeRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError('Voice input not supported. Please use Chrome.'); return; }

    // Stop any existing recognition first
    stopListening();

    const recognition = new SR();
    recognition.continuous     = false;
    recognition.interimResults = true;
    recognition.lang           = LANGUAGES.find(l => l.code === langRef.current)?.recognition || 'en-US';

    recognition.onstart = () => setCallState('listening');

    recognition.onresult = (e) => {
      const result = e.results[e.results.length - 1];
      const text   = result[0].transcript;
      setTranscript(text);
      if (result.isFinal && text.trim()) {
        recognition.stop();
        sendToAI(text.trim());
      }
    };

    recognition.onerror = (e) => {
      if (e.error === 'no-speech' && stateRef.current === 'listening' && !muteRef.current && activeRef.current) {
        setTimeout(() => startListening(), 100);
      }
    };

    recognition.onend = () => {
      // Only auto-restart when in listening state (NOT during speaking/processing)
      if (stateRef.current === 'listening' && !muteRef.current && activeRef.current) {
        setTimeout(() => startListening(), 200);
      }
    };

    recogRef.current = recognition;
    try { recognition.start(); } catch {}
  }, [sendToAI, stopListening]);

  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

  // ── Speak AI response ─────────────────────────────────────────────────────
  const speakResponse = useCallback(async (text) => {
    // ── CRITICAL: stop microphone BEFORE playing audio ──
    // This prevents the mic from picking up the AI's own voice (echo loop).
    stopListening();
    setCallState('speaking');

    const onSpeakEnd = () => {
      if (stateRef.current === 'speaking') {
        setCallState('listening');
        // 400ms buffer — lets speaker audio clear from the room before mic re-activates
        if (!muteRef.current) setTimeout(() => startListening(), 400);
      }
    };

    if (hasTts) {
      try {
        const res = await fetch(`${API_BASE_URL}/customer/ai-chat/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          // Detect language from AI response text (not from mic language)
          body: JSON.stringify({ text, language: detectTtsLang(text, langRef.current) }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          if (res.status === 403 && errData?.code === AI_MEMBERSHIP_REQUIRED_CODE) {
            setShowRenewalDialog(true);
            refreshUser?.();
          }
          throw new Error();
        }
        const blob  = await res.blob();
        const url   = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { URL.revokeObjectURL(url); onSpeakEnd(); };
        audio.onerror = onSpeakEnd;
        audio.play();
        return;
      } catch { /* fallback */ }
    }

    // Browser speechSynthesis fallback — use the detected language locale
    const ttsBrowserLocale = LANGUAGES.find(l => l.code === detectTtsLang(text, langRef.current))?.ttsLocale || 'en-US';
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = ttsBrowserLocale;
    utter.rate = 1.05;
    utter.onend = onSpeakEnd;
    window.speechSynthesis.speak(utter);
  }, [hasTts, token, startListening, stopListening, refreshUser]);

  useEffect(() => { speakResponseRef.current = speakResponse; }, [speakResponse]);

  // ── Start call ────────────────────────────────────────────────────────────
  const startCall = useCallback(async () => {
    if (!aiMembershipActive) {
      setShowRenewalDialog(true);
      return;
    }
    // Acquire an echo-cancelled audio stream first.
    // Even though SpeechRecognition uses its own pipeline, calling getUserMedia
    // with echoCancellation:true activates OS-level echo suppression which
    // Chromium-based browsers also apply to SpeechRecognition.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
        },
      });
      mediaStreamRef.current = stream;
    } catch {
      // Proceed without — recognition will still work, echo cancellation just won't be guaranteed
    }

    setActive(true);
    activeRef.current = true;
    setError('');
    setCallState('listening');
    startListening();
  }, [startListening, aiMembershipActive]);

  // ── End call ──────────────────────────────────────────────────────────────
  const endCall = useCallback(() => {
    activeRef.current = false;
    stopListening();
    interruptSpeech();
    window.speechSynthesis.cancel();

    // Release echo-cancelled stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }

    setActive(false);
    setCallState('idle');
    setTranscript('');
    setAiText('');
    setError('');
    sessionRef.current = null;
  }, [interruptSpeech, stopListening]);

  // ── Tap to interrupt (manual barge-in) ────────────────────────────────────
  const handleInterrupt = useCallback(() => {
    interruptSpeech();
    setCallState('listening');
    setTimeout(() => startListening(), 100);
  }, [interruptSpeech, startListening]);

  // ── Toggle mute ───────────────────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    setMuted(v => {
      const nowMuted = !v;
      muteRef.current = nowMuted;
      if (nowMuted) {
        stopListening();
        if (stateRef.current !== 'speaking') setCallState('muted');
      } else {
        if (stateRef.current === 'muted') {
          setCallState('listening');
          setTimeout(() => startListening(), 100);
        }
      }
      return nowMuted;
    });
  }, [startListening, stopListening]);

  const orbState = !active ? 'idle' : muted && callState !== 'speaking' ? 'muted' : callState;

  return (
    <>
    <div className="min-h-screen bg-[#0f0f13] flex flex-col items-center justify-between py-10 px-4">

      {/* Top: timer + status */}
      <div className="flex flex-col items-center gap-1">
        {active && (
          <span className="text-white/40 text-sm font-mono tracking-widest">{duration}</span>
        )}
        <span className={cn(
          'text-sm font-medium tracking-wide transition-all duration-500',
          callState === 'listening'  ? 'text-blue-400'    :
          callState === 'processing' ? 'text-amber-400'   :
          callState === 'speaking'   ? 'text-emerald-400' :
          callState === 'muted'      ? 'text-muted-foreground'   :
          'text-white/40'
        )}>
          {STATUS_LABEL[active ? callState : 'idle']}
        </span>
      </div>

      {/* Center: Orb + transcript */}
      <div className="flex flex-col items-center gap-6">
        <Orb state={orbState} />

        {/* Transcript / AI text */}
        <div className="h-14 flex items-center justify-center px-4">
          {callState === 'listening' && transcript && (
            <p className="text-white/70 text-sm text-center italic max-w-xs">
              &ldquo;{transcript}&rdquo;
            </p>
          )}
          {callState === 'speaking' && aiText && (
            <p className="text-emerald-300/70 text-sm text-center max-w-xs line-clamp-2">
              {aiText.slice(0, 120)}{aiText.length > 120 ? '...' : ''}
            </p>
          )}
          {callState === 'processing' && (
            <p className="text-amber-400/70 text-sm text-center">Processing...</p>
          )}
        </div>

        {/* Tap to interrupt — shown only while AI is speaking */}
        {active && callState === 'speaking' && (
          <button
            onClick={handleInterrupt}
            data-testid="interrupt-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-card/10 border border-white/20 text-white/80 text-sm hover:bg-card/15 transition-all active:scale-95"
          >
            <Hand className="w-4 h-4" />
            Tap to interrupt
          </button>
        )}

        {/* Error */}
        {error && (
          <p className="text-rose-400 text-xs text-center max-w-xs px-4 py-2 bg-rose-500/10 rounded-lg border border-rose-500/20">
            {error}
          </p>
        )}

        {/* Meal logged notification */}
        {mealLogged && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/15 border border-emerald-500/30 rounded-xl text-xs text-emerald-300">
            <span className="font-medium">Logged:</span>
            <span>{mealLogged.food_name} (~{mealLogged.estimated_calories} kcal) · {mealLogged.meal_time_name}</span>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="flex flex-col items-center gap-6 w-full max-w-xs">
        {!active ? (
          <>
            {/* Mic language selector — controls speech recognition only */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] text-white/50">Mic:</span>
              <select
                value={language}
                onChange={e => setLanguage(e.target.value)}
                data-testid="mic-language-select"
                className="text-[11px] bg-card/10 border border-white/20 rounded-lg px-2.5 py-1.5 text-white/70 focus:outline-none cursor-pointer appearance-none"
                style={{ backgroundImage: 'none' }}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code} style={{ background: '#1a1a1f', color: '#fff' }}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-white/35">· AI auto-detects language</span>
            </div>

            <button
              onClick={startCall}
              data-testid="start-voice-call-btn"
              className="w-full py-4 rounded-2xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-base transition-all duration-200 shadow-lg shadow-violet-500/30 active:scale-95"
            >
              Start Voice Call
            </button>
          </>
        ) : (
          <div className="flex items-center justify-center gap-5">
            {/* Mute */}
            <button
              onClick={toggleMute}
              data-testid="mute-btn"
              className={cn(
                'w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90',
                muted
                  ? 'bg-rose-500/20 border border-rose-500/40 text-rose-400'
                  : 'bg-card/10 border border-white/20 text-white/80 hover:bg-card/15'
              )}
            >
              {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            {/* End call */}
            <button
              onClick={endCall}
              data-testid="end-call-btn"
              className="w-16 h-16 rounded-full bg-rose-500 hover:bg-rose-400 text-white flex items-center justify-center transition-all duration-200 shadow-lg shadow-rose-500/40 active:scale-90"
            >
              <PhoneOff className="w-6 h-6" />
            </button>

            {/* Speaker indicator */}
            <div className="w-14 h-14 rounded-full bg-card/10 border border-white/20 text-white/80 flex items-center justify-center">
              <Volume2 className="w-5 h-5" />
            </div>
          </div>
        )}

        <button
          onClick={() => navigate('/customers/ai-chat')}
          className="text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          Switch to text chat
        </button>
      </div>
    </div>

    <AiMembershipRenewalDialog
      open={showRenewalDialog}
      onOpenChange={setShowRenewalDialog}
      expiryDate={user?.ai_plan_expiry}
      websiteUrl={branding?.website}
    />
    </>
  );
}
